<template>
	<div class="content" :class="{ split: splitView }">
		<Head style="padding-left: 20px;">
			<EntryFilter v-model="filter" :def="def" />
			<div>
				<button class="create" @click="$refs.create.open()" style="display: block;" data-cy="create">
					Add Entry
					<mdi chevron-down />
				</button>
				<Menu ref="create" right="20px" :autoCloseOnClick="false">
					<TypePickerList :suggestedTypeName="filter.contentType"
						@select="create($event)"
						@close="$refs.create.close()"
					/>
				</Menu>
			</div>
		</Head>
		<div class="body">
			<Filters
				class="filters"
				:currentFilter="filter"
				style="position: fixed; top: 100px; bottom: 0; left: 0; overflow: auto; padding-bottom: 60px;"
				@setFilter="setFilter"
			/>
			<div class="entryTableContainer">
				<EntryTable
					:filter="filter"
					:selectable="false"
					@input="selected"
					:initialSkip="skip"
					@skipChanged="skipChanged"
				/>
			</div>
		</div>

		<!-- split view: the entry editor stack overlays the whole list; the list stays mounted
		     underneath as persistent context, revealed again by popping the stack down to empty -->
		<StackingView
			v-if="splitView && currentEntryId"
			ref="stack"
			class="splitOverlay"
			urlMode="query"
			:link="{ id: currentEntryId, type: 'Entry' }"
			@close="onStackClose"
		/>
		<div class="bottom-toolbar">
			<button class="split-toggle" :class="{ active: splitView }" @click="toggleSplit" data-cy="split-toggle" title="Toggle split view">
				<mdi view-split-vertical />
				<span>Split</span>
			</button>
			<ExtensionSlot type="contentList" location="toolbar" />
		</div>
		<div v-if="splitView" class="split-help">
			<div>
				<mdi view-split-vertical crystal /><br />
				You have entered split view.<br />
				Select items from the sidebar to open the details here.<br />
				<br />
				<div style="color: gray;">BETA: This feature is currently in testing</div>
			</div>
		</div>
	</div>
</template>

<script>
import EntryFilter from '../components/fields/EntryFilter.vue'
import EntryTable from '../components/fields/EntryTable.vue'
import StackingView from '../components/StackingView.vue'
import Menu from '../components/Menu.vue'
import TypePickerList from '../components/TypePickerList.vue'
import Head from './Head.vue'
import ProviderMixin from './ProviderMixin.js'
import EntryApiMixin from '../components/EntryApiMixin.js'
import Filters from './Filters.vue'
import { filterFromQuery } from '../components/fields/FilterUtil'
import { visToParam } from '../components/VisualisationUtil'

export default {
	name: 'Content',
	components: { EntryFilter, EntryTable, StackingView, TypePickerList, Menu, Head, Filters },
	mixins: [ ProviderMixin, EntryApiMixin ],
	inject: [ 'base', 'endpoint', 'spaceId', 'environment', 'permissions' ],
	data: () => ({
		filter: { contentType: null, contentTypes: null, search: '', filters: [], columns: null, order: null, vis: [] },
		// TODO: ??
		def: {"id":"x","name":"x","type":"Array","localized":false,"required":false,"validations":[],"disabled":false,"omitted":false,"items":{"type":"Link","validations":[],"linkType":"Entry"}},
		sidebarTab: 'filters',
		skip: 0,
	}),
	computed: {
		// split view is a mode of this same route, flagged by the `split` query param
		splitView() { return 'split' in this.$route.query },
		// the entry currently open in the overlay editor (undefined => list is visible)
		currentEntryId() { return this.$route.query.entry },
	},
	watch: {
		filter: {
			deep: true,
			handler(filter) {
				this.filterChanged()
			},
		},
	},
	methods: {
		routerBeforeEach(to, from, next) {
			// in split view the overlay editor stack manages its own history reconciliation
			if (this.$refs.stack) {
				this.$refs.stack.routerBeforeEach(to, from, next)
				return
			}
			// TODO: if we only changed the filter -> return here
			next()
		},
		selected(entries) {
			const id = entries[0].sys.id
			if (this.splitView) {
				// open the entry in the overlay stack without leaving the list route
				this.$router.push({ path: this.$route.path, query: { ...this.$route.query, entry: id } })
			}
			else {
				this.$router.push(this.base + '/entries/' + id)
			}
		},
		toggleSplit() {
			const query = { ...this.$route.query }
			if (this.splitView) {
				delete query.split
				delete query.entry
				delete query.stack
			}
			else {
				query.split = null
			}
			this.$router.replace({ query }).catch(() => {})
		},
		// the last editor in the overlay stack was closed -> drop entry/stack, revealing the list
		onStackClose() {
			const query = { ...this.$route.query }
			delete query.entry
			delete query.stack
			this.$router.push({ path: this.$route.path, query }).catch(() => {})
		},
		skipChanged(skip) {
			this.skip = skip
			this.filter.skip = skip
			this.filterChanged()
		},
		async create(contentType) {
			const entry = await this.createEntry(contentType.sys.id)
			this.$router.push(this.base + '/entries/' + entry.sys.id)
		},
		setFilter(filter) {
			this.filter.contentType = filter.contentType
			this.filter.contentTypes = filter.contentTypes
			this.filter.filters = filter.filters
			this.filter.search = filter.search
			this.filter.columns = filter.columns
			this.filter.order = filter.order
			this.filter.vis = filter.vis
			this.filter.skip = filter.skip ?? 0
		},
		filterChanged() {
			const filter = this.filter
			// TODO: maybe this piece of code should be generalized somewhere
			//       potentially Filters should emit change events containing this data
			// symmetric to Filters.vue
			const filterQuery = {
				contentType: filter.contentType,
				contentTypes: filter.contentTypes,
				search: filter.search,
				columns: filter.columns?.map?.(column => column.id) ?? [],
				vis: visToParam(filter.vis),
				skip: filter.skip,
				filters: filter.filters.map(f => f.storage),
			}
			// merge onto the existing query so split-view control params (split/entry/stack) survive
			const query = { ...this.$route.query, ...filterQuery }
			// if the url would be the same, dont update router
			if (JSON.stringify(query) != JSON.stringify(this.$route.query))
				this.$router.replace({ query }).catch(() => {})
			localStorage['filterQuery'] = JSON.stringify({ ...filterQuery, spaceId: this.spaceId, environment: this.environment })
		},
	},
	// this needs to happen early, before the table is created so skip is transported there
	created() {
		// TODO-3: add name of space
		document.title = 'Content'

		let view = this.$route.query
		if (!view || Object.keys(view).length == 0) {
			view = JSON.parse(localStorage['filterQuery'] ?? '{}')
			if (view.spaceId != this.spaceId || view.environment != this.environment) view = {}
		}
		if (view) {
			this.setFilter(filterFromQuery(view))
			if (view.skip) this.skip = parseInt(view.skip)
		}
	},
}
</script>

<style scoped>
.EntryFilter { flex-grow: 1; position: relative; z-index: 1000; }
h1 { flex-shrink: 0; font-size: 20px; font-weight: 600; margin: 0; padding: 0; width: 250px; padding: 5px 0;}
button.create { white-space: nowrap; background-color: var(--primary); height: 40px; padding: 8px 10px 8px 15px; color: white; border: 0; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; }

/* SPLIT VIEW */
.bottom-toolbar { position: fixed; left: 0; bottom: 0; z-index: 500; display: flex; }
button.split-toggle.active { background-color: #666; color: white; }
/* split view: the list becomes a fixed-width sidebar on the left, the editor stack is
   offset to the right of it (StackingView's own positioning is left untouched) */
.content.split { --split-sidebar: 360px; }
.content.split .filters { display: none; }
/* pin the sidebar list and let it scroll within itself instead of scrolling the page */
.content.split .entryTableContainer { position: fixed; top: 100px; bottom: 0; left: 0; margin: 0 20px; padding-bottom: 60px; width: var(--split-sidebar); overflow-x: auto; overflow-y: auto; }
/* in split mode the overlay must cover the filter bar, so drop the filter below the overlay;
   root-level modals (1000) and dialogs (2000) then stay above the overlay (900) */
.content.split .EntryFilter { z-index: 800; }
.splitOverlay { position: absolute; inset: 0; z-index: 900; background-color: #fafafa; }
.content.split .splitOverlay { left: calc(var(--split-sidebar) + 40px); }
.split-help { text-align: center; position: fixed; inset: 90px 0 0 360px; display: flex; align-items: center; justify-content: center; }

button.create .icon { font-size: 14px; fill: currentColor; vertical-align: middle; }
.entryTableContainer { flex-grow: 1; margin: 20px; margin-left: 280px; }

@media (max-width: 800px) {
	.filters { display: none; }
	.entryTableContainer { margin-left: 20px; }
}
</style>

<style>
/* The button chrome of the bottom toolbar is unscoped on purpose: extensions render
   into it (ExtensionSlot type="contentList" location="toolbar") and a scoped rule
   would not reach a button that lives in another component. */
.bottom-toolbar button { display: flex; align-items: center; gap: 6px; white-space: nowrap; background-color: white; padding: 16px 16px; font-size: 13px; border: none; cursor: pointer; border-top: 1px solid #eee; }
.bottom-toolbar button .mdi { font-size: 16px; line-height: 1; }

.split .TableColumnsMenu { display: none; }
.split .editor { box-shadow: -20px 0 30px rgba(0,0,0,0.25); }
</style>